import clsx from "clsx";
import React from "react";
import {
  Checkbox,
  CheckboxProps,
  FormGroup,
  FormControlLabel,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  BodypartType,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import FiltersOnlyThisButton from "../buttons/FiltersOnlyThisButton";
import { ALL_OPTION, FILTER_OPTIONS, NONE_OPTION } from "./filterUtils";

const useStyles = makeStyles((theme) => ({
  label: {
    color: theme.palette.common.black,
    fontWeight: 500,
    textTransform: "capitalize",
  },
  option: {
    padding: theme.spacing(0, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover button[name='onlyThisButton']": {
      opacity: 1,
    },
    marginRight: 0,
  },
  optionLabel: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
  header: {
    padding: theme.spacing(0, 1, 0.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

type ExerciseFiltersContentProps<T extends BodypartType | MovementPatternType> =
  {
    filters: Record<T, boolean>;
    onChange: (filters: Record<T, boolean>) => void;
    filterTypesLabels: Record<T, string>;
  };

export function ExerciseFiltersContent<
  T extends BodypartType | MovementPatternType,
>({ filters, onChange, filterTypesLabels }: ExerciseFiltersContentProps<T>) {
  const s = useStyles();

  const enumValuesWithoutNone = Object.keys(filters).filter(
    (key) => key !== NONE_OPTION,
  ) as T[];

  const handleChange: CheckboxProps["onChange"] = React.useCallback(
    (event, checked) => {
      const updatedFilters = {
        ...filters,
        [event.target.value]: checked,
      };
      const none = Object.values(updatedFilters).filter(Boolean).length === 0;
      if (none) {
        onChange({
          ...updatedFilters,
          ["NONE"]: true,
        });
      } else {
        onChange(updatedFilters);
      }
    },
    [filters, onChange],
  );

  const handleAllChange = (checked: boolean) => {
    const updatedFilters = Object.keys(filters).reduce(
      (acc, key) => {
        acc[key as keyof Record<T, boolean>] = checked;
        return acc;
      },
      {} as Record<T, boolean>,
    );

    if (!checked) {
      updatedFilters[NONE_OPTION] = true;
    }

    onChange(updatedFilters);
  };
  const handleOnlyThisChange = (type: string) => {
    const updatedFilters: Record<string, boolean> = {};
    Object.keys(filters).forEach((key) => {
      updatedFilters[key] = key === type;
    });
    onChange(updatedFilters as Record<T, boolean>);
  };

  const isAllSelected = Object.keys(filters).every(
    (key) => key === NONE_OPTION || filters[key as T],
  );

  return (
    <>
      <FormGroup>
        {FILTER_OPTIONS.map((value) => (
          <FormControlLabel
            className={s.option}
            key={value}
            classes={{ label: s.optionLabel }}
            control={
              <Checkbox
                color="primary"
                checked={value === ALL_OPTION ? isAllSelected : filters[value]}
                value={value}
                onChange={(e) =>
                  value === ALL_OPTION
                    ? handleAllChange(e.target.checked)
                    : handleChange(e, e.target.checked)
                }
              />
            }
            label={
              <>
                <Typography variant="body1" className={s.label}>
                  {value.toLowerCase()}
                </Typography>
                {value === NONE_OPTION && (
                  <FiltersOnlyThisButton
                    handleOnlyThisChange={() => handleOnlyThisChange(value)}
                  />
                )}
              </>
            }
          />
        ))}
        <Divider />
      </FormGroup>

      {enumValuesWithoutNone.map((type) => (
        <FormGroup key={type}>
          <FormControlLabel
            className={s.option}
            classes={{ label: s.optionLabel }}
            control={
              <Checkbox
                color="primary"
                value={type}
                checked={filters[type]}
                onChange={handleChange}
              />
            }
            label={
              <>
                <Typography variant="body1" className={s.label}>
                  {filterTypesLabels[type]}
                </Typography>
                <FiltersOnlyThisButton
                  handleOnlyThisChange={() => handleOnlyThisChange(type)}
                />
              </>
            }
          />
        </FormGroup>
      ))}
    </>
  );
}
